import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import { Cards } from '../cards';
import { Stacks } from '../stacks';
import * as styles from './content-anchor-menu.module.css';

export function ContentAnchorMenu({ contentAnchorMenuItems, tocContents }) {
	// state
	const [activeTocItem, setActiveTocItem] = useState(null);

	const processScrollEl = useRef();

	const handleScroll = () => {
		const viewportHeight = window.innerHeight;
		const triggerPoint = viewportHeight * 0.25;

		let closestDistance = Infinity;
		let activeAnchor = null;

		contentAnchorMenuItems.forEach((item) => {
			const element = document.getElementById(item.anchor);
			if (element) {
				const rect = element.getBoundingClientRect();
				const distanceToTrigger = Math.abs(rect.top - triggerPoint);

				if (
					rect.top <= triggerPoint &&
					distanceToTrigger < closestDistance
				) {
					closestDistance = distanceToTrigger;
					activeAnchor = item.anchor;
				}
			}
		});

		if (activeAnchor && activeAnchor !== activeTocItem) {
			setActiveTocItem(activeAnchor);
		}
	};

	// set active id on load
	useEffect(() => {
		const initialActiveTocItem = contentAnchorMenuItems[0]?._key;
		setActiveTocItem(initialActiveTocItem);
	}, []);

	// Add scroll event listener
	useEffect(() => {
		window.addEventListener('scroll', handleScroll);

		// Clean up function
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [contentAnchorMenuItems]);

	const handleScrollToContent = ({ event, anchor }) => {
		event.preventDefault();
		const targetElement = document.getElementById(anchor);

		if (targetElement) {
			const elementTop =
				targetElement.getBoundingClientRect().top + window.scrollY;
			const offset = 80; // Adjust for fixed headers etc.

			window.scrollTo({
				top: elementTop - offset,
				behavior: 'smooth',
			});

			history.pushState(null, '', `#${anchor}`);
		}

		if (window?.gtag) {
			console.log('Tracking anchor link click: ', anchor);
			window.gtag('event', 'anchor', {
				send_to: 'G-R0Z6J0E1GE',
				cta_name: anchor,
			});
		}
	};

	const tocModules = tocContents?.map((module, index) => {
		switch (module._type) {
			case 'cards':
				return <Cards key={module._key || index} {...module} />;
			case 'stacks':
				return <Stacks key={module._key || index} {...module} />;
			default:
				return null;
		}
	});

	return (
		<div className={classNames(styles.moduleContainer)}>
			<div className={styles.parent}>
				<div className={styles.tocContainer}>
					<nav className={styles.toc}>
						<ul>
							{contentAnchorMenuItems?.map((step) => {
								const { _key, header, anchor } = step;
								return (
									<li
										className={classNames(
											activeTocItem === anchor &&
												styles.active,
											styles.tocItem
										)}
										key={_key}
									>
										<a
											className={classNames(
												'body-display-sm'
											)}
											href={`#${anchor}`}
											onClick={(event) =>
												handleScrollToContent({
													event,
													anchor,
												})
											}
										>
											{header}
										</a>
									</li>
								);
							})}
						</ul>
					</nav>
				</div>
				<div
					className={styles.contentContainer}
					ref={processScrollEl}
					tabIndex={0}
					role='menu'
				>
					{tocModules}
				</div>
			</div>
		</div>
	);
}
